import DialogService from './dialogs/DialogService';
import { closeJoiningDelayNetworkErrorModal } from './liveMedia.service';
import {
  getValueFromSessionStorage,
  removeValueFromSessionStorage,
  saveValueToSessionStorage
} from './localStorage.service';
import loggerService from './logger.service';
import { getTrackingValues } from './mediaTracking.utils';
import { UserRole } from './user.service';

import analytics from '@/analytics';
import { AGORA, LOCAL_STORAGE_KEYS } from '@/constants';
import store from '@/store';
import { Broadcast } from '@/store/modules/event/types';
import TechCheckLiteModeNoticeDialog from '@/views/event/TechCheck/TechCheckLiteModeNoticeDialog.vue';

// 'stage' is a connected session whose media never flowed, 'stage_join' a session whose signaling never connected.
type FailureSource = 'stage' | 'stage_join' | 'tech_check';

let stopWaitingForRecording: (() => void) | null = null;
let noticeDialogIndex: number | null = null;

function getMainStageChannelId(channelName: string): string {
  return channelName.replace(AGORA.UID_SUFFIX.BACKSTAGE, '');
}

export function isAudienceOnStage(channelName: string): boolean {
  const mainStageChannelId = getMainStageChannelId(channelName);
  const backstageChannelId = mainStageChannelId + AGORA.UID_SUFFIX.BACKSTAGE;

  const { mediaRooms } = store.state.mediaroom;
  const mainStageRole = mediaRooms[mainStageChannelId]?.userRole;
  const backstageRole = mediaRooms[backstageChannelId]?.userRole;

  return (
    mainStageRole === AGORA.ROLE.ATTENDEE && (backstageRole === AGORA.ROLE.ATTENDEE || backstageRole === undefined)
  );
}

function isLiveBroadcast(broadcast?: Partial<Broadcast>): boolean {
  return (
    !!broadcast?.id &&
    broadcast.broadcast_type !== 'SIMULIVE' &&
    broadcast.broadcast_type !== 'LIVE_EXTERNAL' &&
    !(store.state.event.event?.on_demand_mode && broadcast.is_available_on_demand)
  );
}

function isEligibleAttendee(): boolean {
  return (
    store.state.user.currentEventUser?.event_role === UserRole.EVENT_MEMBER &&
    !store.state.event.event?.is_test &&
    !store.getters['event/isBroadcastSpeakerOrOrganizer'] &&
    !store.getters['event/isSpeaker'] &&
    !store.state.user.currentEventUser?.eventUser?.view_as_attendee
  );
}

// HLS support is only detected inside the event wrapper, so it is checked on the broadcast page, not at tech check.
function canPlayLiteMode(): boolean {
  return !!store.state.user.hlsPlaybackSupported;
}

function canAutoSwitchToLiteMode(channelName: string): boolean {
  return (
    store.state.route.fullPath.includes('/broadcast') &&
    isLiveBroadcast(store.getters['event/currentBroadcast']) &&
    isEligibleAttendee() &&
    canPlayLiteMode() &&
    isAudienceOnStage(channelName)
  );
}

export function cancelPendingLiteModeSwitch() {
  stopWaitingForRecording?.();
  stopWaitingForRecording = null;
}

// toggleToLiteMode is ignored while a lite/live switch is already in flight, so wait for it to settle first.
function whenLiteModeIdle(): Promise<void> {
  const isLoading = () => ['LOADINGTOLITEMODE', 'LOADINGTOLIVEMODE'].includes(store.state.user.liteMode.liteModeState);
  if (!isLoading()) {
    return Promise.resolve();
  }
  return new Promise(resolve => {
    const stop = store.watch(
      state => state.user.liteMode.liteModeState,
      () => {
        if (!isLoading()) {
          stop();
          resolve();
        }
      }
    );
  });
}

async function switchToLiteMode(channelName: string, trigger: 'live' | 'went_live', source: FailureSource) {
  const mainStageChannelId = getMainStageChannelId(channelName);
  const mainStageRoom = store.state.mediaroom.mediaRooms[mainStageChannelId];
  analytics.track('lite_mode_auto_switch', {
    ...getTrackingValues(mainStageRoom),
    channelName: mainStageChannelId,
    trigger,
    source
  });

  try {
    // Leaving first marks the room as no longer wanted, so a join still in flight stops retrying and reloading.
    if (mainStageRoom) {
      await store.dispatch('mediaroom/exitRoom', { channelName: mainStageChannelId });
    }
    store.dispatch('event/avroom/clearCaptions');
    await whenLiteModeIdle();
    await store.dispatch('toggleToLiteMode');
    store.dispatch('event/avroom/showLiteModeAutoSwitchedMessage');
  } catch (error) {
    loggerService.error('[AGORA] Automatic switch to lite mode failed', { channelName, trigger, source, error });
  }
}

function switchToLiteModeWhenRecording(channelName: string, source: FailureSource) {
  if (stopWaitingForRecording) {
    return;
  }
  const broadcastId = store.getters['event/currentBroadcast']?.id;

  stopWaitingForRecording = store.watch(
    (_state, getters) => getters['event/currentBroadcast'],
    currentBroadcast => {
      if (currentBroadcast?.id !== broadcastId) {
        cancelPendingLiteModeSwitch();
        return;
      }
      if (currentBroadcast.is_recording) {
        cancelPendingLiteModeSwitch();
        switchToLiteMode(channelName, 'went_live', source);
      }
    },
    { deep: true }
  );
}

// Discussion rooms have no lite mode, so they are declined here and keep the connection failed flow.
export function handleAttendeeConnectionFailure(channelName: string, source: FailureSource = 'stage'): boolean {
  if (!canAutoSwitchToLiteMode(channelName)) {
    return false;
  }

  if (store.getters['event/currentBroadcast'].is_recording) {
    switchToLiteMode(channelName, 'live', source);
  } else {
    switchToLiteModeWhenRecording(channelName, source);
  }
  return true;
}

function getTechCheckFailureKey(): string {
  return `${LOCAL_STORAGE_KEYS.techCheckConnectionFailed}_${store.state.event.event?.id}`;
}

function hasFailedTechCheck(): boolean {
  return !!getValueFromSessionStorage(getTechCheckFailureKey());
}

function closeTechCheckNotice() {
  if (noticeDialogIndex !== null) {
    DialogService.close(noticeDialogIndex);
    noticeDialogIndex = null;
  }
}

// A tech check that connects late was a slow network, not a blocked one.
export function clearTechCheckConnectionFailure() {
  removeValueFromSessionStorage(getTechCheckFailureKey());
  closeTechCheckNotice();
}

function isEligibleTechCheckAttendee(): boolean {
  const broadcasts = store.state.event.event?.agenda?.broadcasts ?? [];
  return store.state.route.name === 'EventSplash' && broadcasts.some(isLiveBroadcast) && isEligibleAttendee();
}

export function handleTechCheckConnectionFailure(): boolean {
  const willUseLiteMode = isEligibleTechCheckAttendee();
  if (hasFailedTechCheck()) {
    return willUseLiteMode;
  }
  saveValueToSessionStorage(getTechCheckFailureKey(), true);

  analytics.track('tech_check_connection_failed', {
    ...getTrackingValues(store.state.mediaroom.mediaRooms[AGORA.TEST_CHANNEL]),
    channelName: AGORA.TEST_CHANNEL,
    willUseLiteMode
  });

  if (willUseLiteMode) {
    closeJoiningDelayNetworkErrorModal();
    closeTechCheckNotice();
    noticeDialogIndex = DialogService.show(
      TechCheckLiteModeNoticeDialog,
      { size: 'small' },
      {
        on: {
          onCancel: () => {
            noticeDialogIndex = null;
          }
        }
      }
    ).index;
  }
  return willUseLiteMode;
}

export function enterBroadcastAfterFailedTechCheck(channelName: string): boolean {
  closeTechCheckNotice();
  const currentBroadcast = store.getters['event/currentBroadcast'];
  if (!hasFailedTechCheck() || !isLiveBroadcast(currentBroadcast) || !isEligibleAttendee() || !canPlayLiteMode()) {
    return false;
  }

  const { liteModeActive } = store.state.user.liteMode;
  if (currentBroadcast.is_recording) {
    if (liteModeActive) {
      return false;
    }
    switchToLiteMode(channelName, 'live', 'tech_check');
    return true;
  }

  if (liteModeActive) {
    store.dispatch('toggleToLiveMode', { litemode_was_not_avalilable: false });
  }
  switchToLiteModeWhenRecording(channelName, 'tech_check');
  return true;
}
